import React, { JSX } from "react";
import { Anchor } from "../../typography/anchors/anchor/anchor";
import { Badge, BadgeStyle } from "components/badges/badge/badge";
import { DataTable } from "components/tables/data-table/data-table";
import { Icon } from "components/icons/icon";
import { Icons } from "components/icons/constants/icons";
import { InstructorLedTrainingType } from "models/enumerations/products/instructor-led-training-type";
import { PublishStatus } from "models/enumerations/publish-status/publish-status";
import { RouteUtils } from "utilities/route-utils";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useViewEventRoutes } from "utilities/hooks/models/events/use-view-event-routes";
import { t } from "utilities/localization/t";
import { InstructorLedTrainingTypeBadge } from "components/events/instructor-led-training-type-badge/instructor-led-training-type-badge";
import { OrganizationEventRecord } from "models/view-models/contracts/organization-event-record";
import { DateUtils } from "utilities/date-utils";
import "./organization-event-list.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface EventListProps {
    events: OrganizationEventRecord[];
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "organization-event-list";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const OrganizationEventList: React.FC<EventListProps> = ({ events }): JSX.Element => {
    const { record: globalState } = useGlobalState();
   
    const { viewEventRoutes } = useViewEventRoutes({
        roleType: globalState?.currentIdentity?.role?.roleType,
    });

    const getManageEventPath = (event: OrganizationEventRecord) => {
        const manageEventRoute =
            event.instructorLedTrainingType === InstructorLedTrainingType.LiveVirtual
                ? RouteUtils.replacePathParams(viewEventRoutes.details, {
                      id: event.id,
                  })
                : RouteUtils.replacePathParams(viewEventRoutes.details, {
                      id: event.id,
                  });

        return manageEventRoute;
    };

    const getEventStatusBadge = (event: OrganizationEventRecord) => {
        if (event.status === PublishStatus.Canceled) {
            return <Badge style={BadgeStyle.Error} text={t("canceled")} />;
        }

        if (event.status === PublishStatus.Draft) {
            return <Badge style={BadgeStyle.Default} text={t("draft")} />;
        }

        if (event.status === PublishStatus.Live) {
            if (event.isLive()) {
                return <Badge style={BadgeStyle.Success} text={t("live")} />;
            } else {
                return <Badge style={BadgeStyle.Success} text={t("completed")} />;
            }
        }

        return <Badge style={BadgeStyle.Default} text={t("draft")} />;
    };

    return (
        <>
            <DataTable cssClassName={CSS_CLASS_NAME}>
                <thead>
                    <tr>
                        <th className="id">{t("id")}</th>
                        <th className="name">{t("eventName")}</th>
                        <th className="product">{t("product")}</th>
                        <th className="type">{t("type")}</th>
                        <th className="start-date">{t("startDate")}</th>
                        <th className="status">{t("status")}</th>
                        <th className="enrollment">{t("enrollment")}</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map((event) => (
                        <tr key={event.id}>
                            <td className="id">{event.id}</td>
                            <td className="name">
                                <Anchor
                                    path={getManageEventPath(event)}
                                    text={event.name}
                                    cssClassName={`${CSS_CLASS_NAME}__event-name`}
                                />
                            </td>
                            <td className="product">{event.productName}</td>
                            <td className="type">
                                {event.instructorLedTrainingType != null && (
                                    <InstructorLedTrainingTypeBadge
                                        trainingType={event.instructorLedTrainingType}
                                    />
                                )}
                            </td>
                            <td className="start-date">
                                {DateUtils.formatDateCustom(
                                    event.startDate?.toString(),
                                    t("shortDateFormat")
                                )} {" "}
                                {event.isMultipleDays ? (
                                    <Icon type={Icons.CalendarMulti} />
                                ) : (
                                    <Icon type={Icons.CalendarSingle} />
                                )}
                            </td>
                            <td className="status">{getEventStatusBadge(event)}</td>
                            <td className="enrollment">{event.totalEnrollments} / {event.enrollmentLimit}</td>
                        </tr>
                    ))}
                </tbody>
            </DataTable>
        </>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { OrganizationEventList };

// #endregion Exports
